import React from 'react'
import { QRCodeSVG } from 'qrcode.react'

// ── Types ──────────────────────────────────────────────

export type CouponCardData = {
  code: string
  type: 'USER' | 'PARTNER'
  discount_pct: number
  valid_from: string
  valid_until: string
  partner_name?: string | null
  marketing_title?: string
  is_active: boolean
}

// ── Helpers ────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

// ── Styles ─────────────────────────────────────────────

const styles = {
  card: {
    width: '480px',
    borderRadius: '1rem',
    overflow: 'hidden',
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    fontFamily: 'Arial, sans-serif',
    background: 'white',
  } as React.CSSProperties,
  header: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: 'white',
    padding: '1.5rem 2rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,
  logo: {
    fontSize: '1.5rem',
    fontWeight: 800,
    letterSpacing: '0.05em',
    marginBottom: '0.25rem',
  } as React.CSSProperties,
  marketingTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    opacity: 0.95,
    marginTop: '0.5rem',
  } as React.CSSProperties,
  body: {
    padding: '1.5rem 2rem',
    display: 'flex',
    gap: '1.5rem',
    alignItems: 'center',
  } as React.CSSProperties,
  info: {
    flex: 1,
  } as React.CSSProperties,
  codeBox: {
    display: 'inline-block',
    padding: '0.5rem 1rem',
    background: '#f0fdf4',
    border: '2px dashed #22c55e',
    borderRadius: '0.5rem',
    fontFamily: 'monospace',
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#166534',
    letterSpacing: '0.1em',
    marginBottom: '1rem',
  } as React.CSSProperties,
  discountBadge: {
    display: 'inline-block',
    padding: '0.3rem 0.75rem',
    background: '#22c55e',
    color: 'white',
    borderRadius: '9999px',
    fontSize: '1rem',
    fontWeight: 700,
    marginBottom: '1rem',
  } as React.CSSProperties,
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.3rem 0',
    fontSize: '0.85rem',
    color: '#475569',
  } as React.CSSProperties,
  detailLabel: {
    fontWeight: 600,
    color: '#334155',
  } as React.CSSProperties,
  qrContainer: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.5rem',
  } as React.CSSProperties,
  qrLabel: {
    fontSize: '0.7rem',
    color: '#94a3b8',
    textAlign: 'center' as const,
  } as React.CSSProperties,
  footer: {
    background: '#f8fafc',
    borderTop: '1px solid #e2e8f0',
    padding: '0.75rem 2rem',
    textAlign: 'center' as const,
    fontSize: '0.8rem',
    color: '#64748b',
  } as React.CSSProperties,
  typeBadge: (type: 'USER' | 'PARTNER'): React.CSSProperties => ({
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500,
    background: type === 'PARTNER' ? '#eff6ff' : '#fdf4ff',
    color: type === 'PARTNER' ? '#1d4ed8' : '#7c3aed',
    marginBottom: '0.75rem',
  }),
  inactive: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-15deg)',
    fontSize: '2rem',
    fontWeight: 800,
    color: 'rgba(220, 38, 38, 0.3)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.15em',
    pointerEvents: 'none' as const,
  } as React.CSSProperties,
}

// ── Component ──────────────────────────────────────────

type CouponCardProps = {
  coupon: CouponCardData
}

const CouponCard: React.FC<CouponCardProps> = ({ coupon }) => {
  const qrUrl = `https://stringnow.ch/order/start?coupon=${encodeURIComponent(coupon.code)}`
  const marketingTitle =
    coupon.marketing_title || `-${coupon.discount_pct}% sur votre cordage`

  return (
    <div style={{ ...styles.card, position: 'relative' }} id="coupon-card">
      {/* Watermark si inactif */}
      {!coupon.is_active && <div style={styles.inactive}>Désactivé</div>}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logo}>🎾 StringNow</div>
        <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>
          Cordage professionnel
        </div>
        <div style={styles.marketingTitle}>{marketingTitle}</div>
      </div>

      {/* Body */}
      <div style={styles.body}>
        <div style={styles.info}>
          <span style={styles.typeBadge(coupon.type)}>{coupon.type}</span>
          <br />
          <div style={styles.discountBadge}>-{coupon.discount_pct}%</div>
          <br />
          <div style={styles.codeBox}>{coupon.code}</div>

          <div style={{ marginTop: '0.75rem' }}>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Valable du</span>
              <span>{formatDate(coupon.valid_from)}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Jusqu'au</span>
              <span>{formatDate(coupon.valid_until)}</span>
            </div>
            {coupon.partner_name && (
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Partenaire</span>
                <span>{coupon.partner_name}</span>
              </div>
            )}
          </div>
        </div>

        {/* QR Code */}
        <div style={styles.qrContainer}>
          <QRCodeSVG value={qrUrl} size={110} level="M" />
          <div style={styles.qrLabel}>Scanner pour réserver</div>
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        Utilisez ce coupon sur <strong>stringnow.ch</strong>
      </div>
    </div>
  )
}

export default CouponCard
